import React from 'react';
import { motion } from 'framer-motion';
import { Users, ClipboardList, DollarSign, ScrollText, ChartArea, Ruler, Box } from 'lucide-react';
import customerDatabaseImage from '../assets/how-it-works/customer-database-dashboard.jpg';
import orderManagementImage from '../assets/how-it-works/order-management-workshop.jpg';
import paymentProcessingImage from '../assets/how-it-works/payment-processing-terminal.jpg';
import invoiceGenerationImage from '../assets/how-it-works/invoice-generation-laptop.jpg';
import analyticsChartsImage from '../assets/how-it-works/analytics-charts.jpg';
import measurementRecordingImage from '../assets/how-it-works/measurement-recording-tablet.jpg';
import inventoryManagementImage from '../assets/how-it-works/inventory-management-fabric.jpg';

const HowItWorksBusinessesSection: React.FC = () => {
  const features = [
    {
      icon: Users,
      title: 'Comprehensive Customer Database',
      description: 'Maintain detailed profiles of all your clients, including their preferences and order history.',
      image: customerDatabaseImage,
      imageAlt: 'Customer database dashboard',
      layout: 'left', // icon+text on left, image on right
    },
    {
      icon: ClipboardList,
      title: 'Efficient Order Management',
      description: 'Streamline your workflow with tools for tracking, scheduling, and prioritizing tailoring orders.',
      image: orderManagementImage,
      imageAlt: 'Tailor working in workshop',
      layout: 'right', // image on left, icon+text on right
    },
    {
      icon: DollarSign,
      title: 'Seamless Payment Processing',
      description: 'Accept payments smoothly, generate invoices, and reconcile transactions effortlessly.',
      image: paymentProcessingImage,
      imageAlt: 'Payment terminal with credit card',
      layout: 'left',
    },
    {
      icon: ScrollText,
      title: 'Automated Invoice Generation',
      description: 'Create professional, branded invoices automatically for all completed services.',
      image: invoiceGenerationImage,
      imageAlt: 'Laptop showing invoice document',
      layout: 'right',
    },
    {
      icon: ChartArea,
      title: 'Performance Analytics',
      description: 'Gain insights into your business performance with detailed reports on sales, customer trends, and more.',
      image: analyticsChartsImage,
      imageAlt: 'Analytics charts and graphs',
      layout: 'left',
    },
    {
      icon: Ruler,
      title: 'Precise Measurement Recording',
      description: 'Digitally record and store client measurements for accurate and consistent tailoring.',
      image: measurementRecordingImage,
      imageAlt: 'Tablet showing 3D body measurements',
      layout: 'right',
    },
    {
      icon: Box,
      title: 'Integrated Inventory Management',
      description: 'Track fabric, notions, and supplies effortlessly to ensure you always have what you need.',
      image: inventoryManagementImage,
      imageAlt: 'Fabric shelves with inventory management',
      layout: 'left',
    },
  ];

  return (
    <section className="bg-white py-16 lg:py-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingLeft: 'clamp(16px, 8.89vw, 128px)', paddingRight: 'clamp(16px, 8.89vw, 128px)' }}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-opensans text-[36px] leading-[40px] text-brand-blue font-bold mb-4"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Businesses: Elevate Your Tailoring Service
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-manrope text-[18px] leading-[28px] text-text-secondary max-w-[768px] mx-auto"
          >
            Streamline operations, manage clients, and grow your tailoring enterprise with powerful, intuitive tools.
          </motion.p>
        </div>

        {/* Features with Alternating Layout */}
        <div className="space-y-24">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isLeftLayout = feature.layout === 'left';

            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  !isLeftLayout ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Icon and Text Content */}
                <div className={isLeftLayout ? '' : 'lg:col-start-2'}>
                  <div className="flex items-start gap-6">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <Icon className="w-12 h-12 text-brand-yellow" />
                      </div>
                    </div>

                    {/* Text Content */}
                    <div className="flex-1">
                      <h3 
                        className="font-opensans text-[24px] leading-[32px] text-text-primary font-bold mb-4"
                        style={{ fontVariationSettings: "'wdth' 100" }}
                      >
                        {feature.title}
                      </h3>
                      <p className="font-manrope text-[18px] leading-[28px] text-text-secondary max-w-[488px]">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={isLeftLayout ? '' : 'lg:col-start-1 lg:row-start-1'}>
                  <div className="relative rounded-lg overflow-hidden w-full max-w-[488px]" style={{ height: '325px' }}>
                    <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-lg">
                      <img 
                        src={feature.image} 
                        alt={feature.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksBusinessesSection;
